import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { fetchFeaturedArticles as fetchFeaturedArticlesApi, fetchHeroSlides as fetchHeroSlidesApi } from '../../util/api';

export const ASSET_NAME = 'landing-page';

const useHostedLandingPage = process.env.REACT_APP_USE_HOSTED_LANDING_PAGE === 'true';

const RESULT_PAGE_SIZE = 8;

const queryListingsPayloadCreator = (
  { config, perPage = RESULT_PAGE_SIZE },
  { dispatch, rejectWithValue, extra: sdk }
) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const queryParams = {
    perPage,
    minStock: 1,
    meta_isFeatured: true,
    include: ['author', 'images'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  return sdk.listings
    .query(queryParams)
    .then(response => {
      // If no featured listings, fallback to latest
      if (response.data.data.length === 0) {
        const { meta_isFeatured, ...fallbackParams } = queryParams;
        return sdk.listings.query(fallbackParams);
      }
      return response;
    })
    .then(response => {
      const listings = response.data.data;
      const listingRefs = listings.map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      return { listingRefs, totalItems: response.data.meta.totalItems };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryListingsThunk = createAsyncThunk(
  'LandingPage/queryListings',
  queryListingsPayloadCreator
);

export const queryListings = (config, perPage) => dispatch => {
  return dispatch(queryListingsThunk({ config, perPage }));
};

// Featured articles thunk
const fetchFeaturedArticlesPayloadCreator = async (_, { rejectWithValue }) => {
  try {
    const response = await fetchFeaturedArticlesApi();
    return response.data;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchFeaturedArticlesThunk = createAsyncThunk(
  'LandingPage/fetchFeaturedArticles',
  fetchFeaturedArticlesPayloadCreator
);

export const fetchFeaturedArticles = () => dispatch => {
  return dispatch(fetchFeaturedArticlesThunk());
};

// Hero slides thunk
const fetchHeroSlidesPayloadCreator = async (_, { rejectWithValue }) => {
  try {
    const response = await fetchHeroSlidesApi();
    return response.data;
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchHeroSlidesThunk = createAsyncThunk(
  'LandingPage/fetchHeroSlides',
  fetchHeroSlidesPayloadCreator
);

export const fetchHeroSlides = () => dispatch => {
  return dispatch(fetchHeroSlidesThunk());
};

const initialState = {
  listingRefs: [],
  queryListingsError: null,
  queryListingsInProgress: false,
  totalItems: 0,
  featuredArticles: [],
  featuredArticlesError: null,
  featuredArticlesInProgress: false,
  heroSlides: [],
  heroSlidesError: null,
  heroSlidesInProgress: false,
};

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(queryListingsThunk.pending, state => {
        state.queryListingsInProgress = true;
        state.queryListingsError = null;
      })
      .addCase(queryListingsThunk.fulfilled, (state, action) => {
        state.queryListingsInProgress = false;
        state.listingRefs = action.payload.listingRefs;
        state.totalItems = action.payload.totalItems;
      })
      .addCase(queryListingsThunk.rejected, (state, action) => {
        state.queryListingsInProgress = false;
        state.queryListingsError = action.payload;
      })
      // Featured articles
      .addCase(fetchFeaturedArticlesThunk.pending, state => {
        state.featuredArticlesInProgress = true;
        state.featuredArticlesError = null;
      })
      .addCase(fetchFeaturedArticlesThunk.fulfilled, (state, action) => {
        state.featuredArticlesInProgress = false;
        state.featuredArticles = action.payload;
      })
      .addCase(fetchFeaturedArticlesThunk.rejected, (state, action) => {
        state.featuredArticlesInProgress = false;
        state.featuredArticlesError = action.payload;
      })
      // Hero slides
      .addCase(fetchHeroSlidesThunk.pending, state => {
        state.heroSlidesInProgress = true;
        state.heroSlidesError = null;
      })
      .addCase(fetchHeroSlidesThunk.fulfilled, (state, action) => {
        state.heroSlidesInProgress = false;
        state.heroSlides = action.payload;
      })
      .addCase(fetchHeroSlidesThunk.rejected, (state, action) => {
        state.heroSlidesInProgress = false;
        state.heroSlidesError = action.payload;
      });
  },
});

export default landingPageSlice.reducer;

export const loadData = (params, search, config) => dispatch => {
  // Always fetch featured articles for the editorial section
  const featuredArticlesPromise = dispatch(fetchFeaturedArticles());
  // Fetch hero slides for the carousel
  const heroSlidesPromise = dispatch(fetchHeroSlides());

  if (useHostedLandingPage) {
    const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };
    return Promise.all([
      dispatch(fetchPageAssets(pageAsset, true)),
      featuredArticlesPromise,
      heroSlidesPromise,
    ]);
  }

  return Promise.all([
    dispatch(queryListings(config, RESULT_PAGE_SIZE)),
    featuredArticlesPromise,
    heroSlidesPromise,
  ]);
};
